import React, {
  createContext,
  useReducer,
  Suspense,
  useCallback,
  useMemo
} from 'react'
import { t } from './i18n'
import { Modal } from '../components/modals/Modal'

export const ModalsContext = createContext(null)

const reducer = (state, action) => {
  switch (action.type) {
    case 'open':
      const exists = state.some((modal) => modal.name === action.payload.name)
      if (exists) return state
      return [...state, { ...action.payload }]
    case 'close':
      const deleteAmount =
        typeof action.payload === 'number' ? action.payload : -1
      return state.slice(0, deleteAmount)
    default:
      return state
  }
}

interface openModalProps {
  name: string
  props: any
  onModalClose: (any) => void
  modalTitle: string
  modifier: string
  hideCloseButton: boolean
  disableBackdropClose?: boolean
}

export const ModalsProvider = (props) => {
  const [state, dispatch] = useReducer(reducer, [])
  const modalsAmount = state.length

  const openModal = useCallback(
    ({
      name,
      props,
      onModalClose,
      modalTitle,
      modifier,
      hideCloseButton,
      disableBackdropClose
    }: openModalProps) => {
      dispatch({
        type: 'open',
        payload: {
          name,
          props,
          modalTitle,
          onModalClose,
          modifier,
          hideCloseButton,
          disableBackdropClose
        }
      })
    },
    []
  )

  const closeModal = useCallback((deleteAmount?: number) => {
    dispatch({
      type: 'close',
      payload: deleteAmount || -1
    })
  }, [])

  const currentModal = state[modalsAmount - 1]
  const {
    name,
    props: modalProps,
    modalTitle,
    onModalClose,
    modifier,
    hideCloseButton,
    disableBackdropClose
  } = currentModal || {}

  const contextValue = useMemo(
    () => ({
      closeModal,
      openModal,
      onModalClose,
      modifier,
      name
    }),
    [closeModal, openModal, onModalClose, modifier, name]
  )

  const CurrentModal = currentModal
    ? React.lazy(() => import(`../components/modals/${name}.tsx`))
    : null

  return (
    <ModalsContext.Provider value={contextValue}>
      {props.children}
      {CurrentModal && (
        <Suspense fallback={<></>}>
          <Modal
            isOpen={true}
            title={t(modalTitle)}
            onHide={() => {
              closeModal()
              onModalClose?.()
            }}
            modifier={modifier || null}
            hideCloseButton={hideCloseButton}
            disableBackdropClose={disableBackdropClose}
          >
            <CurrentModal {...modalProps} />
          </Modal>
        </Suspense>
      )}
    </ModalsContext.Provider>
  )
}
